import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import _ from 'lodash';
import Checkbox from '../../Checkbox/Checkbox';
import { DropdownContainer, DropdownWrapper } from '../../DropDown/styles';
import { DropdownRow, EmptyState, Label } from './styles';
import { InputPositionType, OVERLAY_MAX_HEIGHT, SelectOverlayProps } from './types';
import { ThemeContext } from '../../ThemeProvider/ThemeProvider';

const root = document.getElementById('overlay-root');
const SelectOverlay: React.FC<SelectOverlayProps> = (props) => {
  const { isMobile } = useContext(ThemeContext);

  const {
    target,
    isOpen,
    onSelection,
    onSelectAll,
    setIsOpen,
    options,
    keyProperty,
    showProperty,
    labelAll,
    chevronRef,
    inputRef,
    overlayPosition = 'auto',
    targetHeight = 0,
    emptyStateLabel = 'Sin resultados',
    overAll = false,
    testId,
  } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const [parentRect, setParentRect] = useState<InputPositionType>({
    width: 0,
    top: 0,
    bottom: 0,
    x: 0,
  });

  const handleClose = useCallback(
    (event) => {
      if (
        !divRef.current.contains(event.target) &&
        !chevronRef?.current?.contains(event.target) &&
        !inputRef?.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    },
    [chevronRef, inputRef, setIsOpen],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClose);
    } else {
      document.removeEventListener('mousedown', handleClose);
    }
    return () => document.removeEventListener('mousedown', handleClose);
  }, [handleClose, isOpen]);

  useEffect(() => {
    const parent = document.getElementById(target);
    const newRect: DOMRect = parent?.getBoundingClientRect();
    if (newRect) {
      setParentRect({
        width: newRect?.width,
        top: newRect?.top + window.scrollY,
        bottom: newRect?.bottom + window.scrollY,
        x: newRect?.x,
      });
    }
  }, [target, isOpen]);

  const position = useMemo(() => {
    const html = document.documentElement;
    const height = Math.max(html.clientHeight, html.scrollHeight);
    const bottomSpace = height - parentRect?.bottom;
    if (overlayPosition === 'top') return parentRect?.top - OVERLAY_MAX_HEIGHT - 6;
    if (overlayPosition === 'bottom') return parentRect?.top + targetHeight + 6;
    return bottomSpace < OVERLAY_MAX_HEIGHT
      ? parentRect?.top - OVERLAY_MAX_HEIGHT - 6
      : parentRect?.top + targetHeight + 6;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayPosition, parentRect?.bottom, parentRect?.top, isOpen]);

  const renderRows = useCallback(() => {
    if (options.length === 0) {
      return <EmptyState>{emptyStateLabel}</EmptyState>;
    }

    return (
      <>
        <DropdownRow
          width={parentRect.width}
          id="select-all"
          data-testId={`${testId}-option-all`}
          onMouseDown={onSelectAll}
        >
          <Checkbox
            size="small"
            value={options.every((ob) => ob.selected)}
            testId={`${testId}-option-all-check`}
          />
          <Label>{labelAll}</Label>
        </DropdownRow>
        {options?.map((option, index) => (
          <DropdownRow
            width={parentRect.width}
            key={_.get(option, keyProperty)}
            id={_.get(option, keyProperty)}
            onMouseDown={onSelection(option)}
            data-testId={`${testId}-option-${index}`}
          >
            <Checkbox
              size="small"
              value={option.selected}
              testId={`${testId}-option-${index}-check`}
            />
            <Label data-testId={`${testId}-option-${index}-label`}>
              {_.get(option, showProperty)}
            </Label>
          </DropdownRow>
        ))}
      </>
    );
  }, [
    emptyStateLabel,
    keyProperty,
    labelAll,
    onSelectAll,
    onSelection,
    options,
    parentRect.width,
    showProperty,
    testId,
  ]);

  return createPortal(
    <DropdownContainer
      ref={divRef}
      isVisible={isOpen}
      top={position}
      width={parentRect.width}
      left={parentRect.x}
      data-testid={`${testId}-overlay`}
      overAll={overAll}
      isMobile={isMobile}
    >
      <DropdownWrapper isMobile={isMobile}>{renderRows()}</DropdownWrapper>
    </DropdownContainer>,
    root,
  );
};

export default SelectOverlay;
